import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  LayoutDashboard,
  Factory,
  Box,
  Cog,
  Calendar,
  LogOut,
  Menu,
  User,
  Settings,
  ChevronDown,
  Wrench,
  Clock,
  Play,
  ClipboardList,
  FileSpreadsheet
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import NotificationCenter from '@/components/NotificationCenter';

// Navigation principale
const mainNavigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
];

// Menu Planification (sous-menu)
const planningNavigation = [
  { name: 'Vue Planning', href: '/planning', icon: Calendar },
  { name: 'Import Excel', href: '/planning/import', icon: FileSpreadsheet },
];

// Menu Paramètres (regroupé)
const settingsNavigation = [
  { name: 'Actions en cours', href: '/current-actions', icon: Play },
  { name: 'Fiche Opérateur', href: '/operator', icon: ClipboardList },
  { name: 'Vue Atelier', href: '/atelier', icon: Factory },
  { name: 'Gestion Îlots', href: '/ilots', icon: Box },
  { name: 'Gestion Machines', href: '/machines', icon: Cog },
  { name: 'Types Machines', href: '/administration', icon: Wrench },
  { name: 'Heures de Travail', href: '/working-hours', icon: Clock },
];

// Admin seulement
const adminNavigation = [
  { name: 'Utilisateurs', href: '/administration/users', icon: User },
];

export const AppLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const isActiveRoute = (href) => location.pathname === href;
  const isPlanningRoute = planningNavigation.some(item => location.pathname === item.href);
  const isSettingsRoute = settingsNavigation.some(item => location.pathname === item.href);
  const isAdminRoute = adminNavigation.some(item => location.pathname === item.href);

  return (
    <div className="min-h-screen bg-background" data-testid="app-layout">
      {/* Header avec navigation horizontale */}
      <header className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b-2 border-border">
        <div className="flex items-center h-16 px-4 lg:px-8">
          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2 hover:bg-secondary rounded-sm mr-2"
            onClick={() => setMobileMenuOpen(true)}
            data-testid="open-mobile-menu"
          >
            <Menu className="w-5 h-5" />
          </button>

          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-3" data-testid="logo-link">
            <div className="w-9 h-9 bg-primary rounded-sm flex items-center justify-center">
              <Factory className="w-5 h-5 text-primary-foreground" strokeWidth={1.5} />
            </div>
            <div className="hidden sm:block">
              <h1 className="font-heading font-bold text-lg tracking-tight leading-none">TAP-CN</h1>
              <p className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">Industrial OS</p>
            </div>
          </Link>

          {/* Navigation desktop */}
          <nav className="hidden lg:flex ml-8 gap-1">
            {/* Liens principaux */}
            {mainNavigation.map((item) => {
              const isActive = isActiveRoute(item.href);
              const Icon = item.icon;

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s/g, '-')}`}
                  className={`
                    flex items-center gap-2 px-3 py-2 rounded-sm text-sm font-medium transition-all
                    ${isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                    }
                  `}
                >
                  <Icon className="w-4 h-4" strokeWidth={1.5} />
                  <span className="uppercase tracking-wide text-xs">{item.name}</span>
                </Link>
              );
            })}

            {/* Menu Planification dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="nav-planning-dropdown"
                  className={`
                    flex items-center gap-2 px-3 py-2 rounded-sm text-sm font-medium transition-all
                    ${isPlanningRoute
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                    }
                  `}
                >
                  <Calendar className="w-4 h-4" strokeWidth={1.5} />
                  <span className="uppercase tracking-wide text-xs">Planification</span>
                  <ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                {planningNavigation.map((item) => {
                  const Icon = item.icon;
                  return (
                    <DropdownMenuItem key={item.href} asChild>
                      <Link
                        to={item.href}
                        data-testid={`nav-${item.name.toLowerCase().replace(/\s/g, '-')}`}
                        className={`flex items-center gap-2 cursor-pointer ${
                          isActiveRoute(item.href) ? 'bg-secondary' : ''
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {item.name}
                      </Link>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Menu Paramètres dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="nav-settings-dropdown"
                  className={`
                    flex items-center gap-2 px-3 py-2 rounded-sm text-sm font-medium transition-all
                    ${isSettingsRoute
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                    }
                  `}
                >
                  <Settings className="w-4 h-4" strokeWidth={1.5} />
                  <span className="uppercase tracking-wide text-xs">Paramètres</span>
                  <ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-52">
                {settingsNavigation.map((item) => {
                  const Icon = item.icon;
                  return (
                    <DropdownMenuItem key={item.href} asChild>
                      <Link
                        to={item.href}
                        data-testid={`nav-${item.name.toLowerCase().replace(/\s/g, '-')}`}
                        className={`flex items-center gap-2 cursor-pointer ${
                          isActiveRoute(item.href) ? 'bg-secondary' : ''
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {item.name}
                      </Link>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Admin dropdown menu (seulement pour admins) */}
            {user?.role === 'admin' && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="nav-admin-dropdown"
                    className={`
                      flex items-center gap-2 px-3 py-2 rounded-sm text-sm font-medium transition-all
                      ${isAdminRoute
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                      }
                    `}
                  >
                    <User className="w-4 h-4" strokeWidth={1.5} />
                    <span className="uppercase tracking-wide text-xs">Admin</span>
                    <ChevronDown className="w-3 h-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48">
                  {adminNavigation.map((item) => {
                    const Icon = item.icon;
                    return (
                      <DropdownMenuItem key={item.href} asChild>
                        <Link
                          to={item.href}
                          data-testid={`nav-${item.name.toLowerCase().replace(/\s/g, '-')}`}
                          className={`flex items-center gap-2 cursor-pointer ${
                            isActiveRoute(item.href) ? 'bg-secondary' : ''
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                          {item.name}
                        </Link>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </nav>

          {/* Right side: notifications + user menu */}
          <div className="ml-auto flex items-center gap-2">
            <NotificationCenter />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="gap-2"
                  data-testid="user-menu-trigger"
                >
                  <Avatar className="w-8 h-8 border border-border">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-secondary text-foreground text-xs">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden md:inline font-medium text-sm">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground font-mono">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-destructive focus:text-destructive cursor-pointer"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Mobile navigation Sheet */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="left" className="w-72 p-0">
          <SheetHeader className="h-16 flex flex-row items-center px-6 border-b-2 border-border">
            <div className="w-9 h-9 bg-primary rounded-sm flex items-center justify-center">
              <Factory className="w-5 h-5 text-primary-foreground" strokeWidth={1.5} />
            </div>
            <SheetTitle className="ml-3 font-heading font-bold text-lg">TAP-CN</SheetTitle>
          </SheetHeader>

          <nav className="p-4 space-y-1">
            {/* Navigation principale mobile */}
            {mainNavigation.map((item) => {
              const isActive = isActiveRoute(item.href);
              const Icon = item.icon;

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={`mobile-nav-${item.name.toLowerCase().replace(/\s/g, '-')}`}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium transition-all
                    ${isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                    }
                  `}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                  <span className="uppercase tracking-wide">{item.name}</span>
                </Link>
              );
            })}

            {/* Section Planification mobile */}
            <div className="my-4 border-t border-border pt-4">
              <p className="px-4 text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">
                Planification
              </p>
            </div>
            {planningNavigation.map((item) => {
              const isActive = isActiveRoute(item.href);
              const Icon = item.icon;

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={`mobile-nav-${item.name.toLowerCase().replace(/\s/g, '-')}`}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium transition-all
                    ${isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                    }
                  `}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                  <span className="uppercase tracking-wide">{item.name}</span>
                </Link>
              );
            })}

            {/* Section Paramètres mobile */}
            <div className="my-4 border-t border-border pt-4">
              <p className="px-4 text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">
                Paramètres
              </p>
            </div>
            {settingsNavigation.map((item) => {
              const isActive = isActiveRoute(item.href);
              const Icon = item.icon;

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={`mobile-nav-${item.name.toLowerCase().replace(/\s/g, '-')}`}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium transition-all
                    ${isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                    }
                  `}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                  <span className="uppercase tracking-wide">{item.name}</span>
                </Link>
              );
            })}

            {/* Admin section mobile */}
            {user?.role === 'admin' && (
              <>
                <div className="my-4 border-t border-border pt-4">
                  <p className="px-4 text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">
                    Administration
                  </p>
                </div>
                {adminNavigation.map((item) => {
                  const isActive = isActiveRoute(item.href);
                  const Icon = item.icon;

                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      data-testid={`mobile-nav-${item.name.toLowerCase().replace(/\s/g, '-')}`}
                      className={`
                        flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium transition-all
                        ${isActive
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                        }
                      `}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Icon className="w-5 h-5" strokeWidth={1.5} />
                      <span className="uppercase tracking-wide">{item.name}</span>
                    </Link>
                  );
                })}
              </>
            )}
          </nav>

          {/* User section at bottom of mobile menu */}
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t-2 border-border">
            <div className="flex items-center gap-3 px-2">
              <Avatar className="w-10 h-10 border-2 border-border">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-primary text-primary-foreground font-heading font-bold">
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{user?.name || 'Utilisateur'}</p>
                <p className="text-xs text-muted-foreground truncate font-mono">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full mt-3 text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Page content */}
      <main className="p-4 lg:p-8">
        {children}
      </main>
    </div>
  );
};

export default AppLayout;
